import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMicrosoftConsentLink, useSetMicrosoft } from "./tenants-api";

/**
 * The tenant's Microsoft 365 organisation, for SharePoint sync
 * (`plans/sharepoint-sync.md`, questions 25 and 26) and Outlook sync
 * (`plans/outlook-sync.md`, question 2: the admin is told, before the click,
 * that the consent lets GroundBrain read mail in every mailbox). We set it up with the
 * customer's admin, in two steps: the admin consents through the link, and
 * the organisation's id is recorded here. After that, any user of the tenant
 * can link a project to a folder they can open in SharePoint themselves.
 *
 * Nothing per site (the owner, 2026-10-19): the app holds
 * `Sites.ReadWrite.All`, every site, so the admin's whole part is the consent,
 * in Entra. The link checks, as the person, that the folder opens for them.
 * The steps to send the admin are `handover/microsoft-365-it-admin-guide.pdf`.
 */
export function MicrosoftTab({
  tenantId,
  organisation,
}: {
  tenantId: string;
  /** The recorded Entra tenant id, or null. */
  organisation: string | null;
}) {
  const [draft, setDraft] = useState(organisation ?? "");
  const set = useSetMicrosoft(tenantId);
  const link = useMicrosoftConsentLink(tenantId);
  const [copied, setCopied] = useState(false);

  const copyLink = async (): Promise<void> => {
    setCopied(false);
    const url = await link.mutateAsync().catch(() => null);
    if (!url) return;
    await navigator.clipboard?.writeText(url).then(
      () => setCopied(true),
      () => undefined,
    );
  };

  return (
    <div className="flex max-w-[720px] flex-col gap-6 text-[0.88rem] text-ink-prose">
      <p>
        {organisation ? (
          <>
            Connected to the Microsoft 365 organisation <span className="font-mono">{organisation}</span>. Its
            projects can be linked to SharePoint folders, and its people can sync folders of their own mailboxes.
          </>
        ) : (
          "No Microsoft 365 organisation is connected. Its projects cannot use SharePoint or Outlook yet."
        )}
      </p>

      <section>
        <h3 className="font-semibold text-ink">1. The admin consents</h3>
        <p className="mt-1 text-muted">
          Send this link to the organisation’s Microsoft 365 admin, with{" "}
          <span className="font-mono">handover/microsoft-365-it-admin-guide.pdf</span>. Tell them first what it
          asks for, because Microsoft’s screen will say “Read and write items in all site collections” and “Read
          mail in all mailboxes”. GroundBrain syncs only the SharePoint folders people link, each one a folder the
          person can open themselves, and only the Outlook folders each person chooses from their own mailbox.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Button variant="secondary" size="sm" disabled={link.isPending} onClick={() => void copyLink()}>
            Copy the consent link
          </Button>
          {copied && <span className="text-[0.8rem] font-semibold text-ok">Copied.</span>}
        </div>
        {link.data && <p className="mt-2 break-all font-mono text-[0.75rem] text-faint">{link.data}</p>}
        {link.error && (
          <p role="alert" className="mt-2 text-[0.8rem] font-semibold text-danger">
            {link.error.message}
          </p>
        )}
      </section>

      <section>
        <h3 className="font-semibold text-ink">2. Record the organisation</h3>
        <p className="mt-1 text-muted">
          After consenting, GroundBrain’s page shows the admin their organisation’s id. Enter it here.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Input
            aria-label="Microsoft organisation id"
            className="max-w-[360px] font-mono"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="3f2b9c1e-6a4d-4b8e-9f10-2c7d5e8a1b40"
          />
          <Button size="sm" disabled={set.isPending || !draft.trim()} onClick={() => set.mutate(draft.trim())}>
            Save
          </Button>
          {organisation && (
            <Button
              variant="secondary"
              size="sm"
              disabled={set.isPending}
              onClick={() => {
                setDraft("");
                set.mutate(null);
              }}
            >
              Disconnect
            </Button>
          )}
        </div>
        {set.error && (
          <p role="alert" className="mt-2 text-[0.8rem] font-semibold text-danger">
            {set.error.message}
          </p>
        )}
      </section>

    </div>
  );
}
